import { Box, Grid, HStack, Skeleton, Text, VStack } from '@chakra-ui/react'
import { useCallback, useMemo } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import { ErrorState } from '@mangovoice/mango-ui'

import type { HotelsApi } from '../api/hotelsApi'
import { EmergencyCallsTable } from '../components/dashboard/EmergencyCallsTable'
import { IncomingCallsTable } from '../components/dashboard/IncomingCallsTable'
import { RoomStatusGrid } from '../components/dashboard/RoomStatusGrid'
import { ResetAllRoomsButton } from '../components/rooms/ResetAllRoomsButton'
import { HOTELS_LARGE_LIST_LIMIT } from '../constants/limits'
import { HotelsI18nProvider, useHotelsI18n } from '../i18n'
import type { HotelsTranslationLike } from '../i18n'
import {
  useListCallHistory,
  useListEmergencyCalls,
  useListExtensionLines,
  useListRooms,
  useListWakeupCallsAll,
  useResetAllRooms,
} from '../queries/hooks'
import type { EnrichedRoom, HospitalityPermissions } from '../types/rooms'
import {
  getRoomDashboardWakeups,
  getWakeupNotificationTone,
} from '../utils/wakeupDashboard'

interface HotelsDashboardPageProps {
  api: HotelsApi
  pbxId: number
  pbxUuid: string
  /** IANA PBX timezone (system settings), normalized in the host app. */
  pbxTimeZone: string
  toast: ToastLike
  permissions: HospitalityPermissions
  t?: HotelsTranslationLike
  locale?: string
}

export const HotelsDashboardPage = ({
  t,
  locale,
  ...props
}: HotelsDashboardPageProps) => {
  return (
    <HotelsI18nProvider t={t} locale={locale}>
      <HotelsDashboardPageInner {...props} />
    </HotelsI18nProvider>
  )
}

const HotelsDashboardPageInner = ({
  api,
  pbxId,
  pbxUuid,
  pbxTimeZone,
  toast,
  permissions,
}: HotelsDashboardPageProps) => {
  const ROOM_SKELETON_COUNT = 18
  const { text } = useHotelsI18n()

  const extensionLinesQuery = useListExtensionLines({
    api,
    pbxId,
    options: { enabled: pbxId > 0, staleTime: 60_000 },
  })

  const roomsQuery = useListRooms({
    api,
    params: {
      pbx_uuid: pbxUuid,
      pbx_id: pbxId,
      limit: HOTELS_LARGE_LIST_LIMIT,
    },
    extensionLines: extensionLinesQuery.data,
    options: {
      enabled: pbxId > 0 && !!pbxUuid,
      refetchInterval: 30_000,
      placeholderData: (previousData) => previousData,
    },
  })

  const rooms: EnrichedRoom[] = Array.isArray(roomsQuery.data?.results)
    ? roomsQuery.data.results
    : []

  const wakeupToneQuery = useListWakeupCallsAll({
    api,
    params: {
      pbx_id: pbxUuid,
      limit: HOTELS_LARGE_LIST_LIMIT,
    },
    options: {
      enabled: !!pbxUuid,
      staleTime: 30_000,
      refetchInterval: 30_000,
      refetchOnWindowFocus: false,
      placeholderData: (previousData) => previousData,
    },
  })

  const callHistoryQuery = useListCallHistory({
    api,
    pbxId,
    options: { refetchInterval: 60_000 },
  })

  const emergencyQuery = useListEmergencyCalls({
    api,
    pbxId,
    options: { refetchInterval: 60_000 },
  })

  const resetMutation = useResetAllRooms({
    api,
    options: {
      onSuccess: (data) => {
        toast.create({
          title: text(
            'hotels.dashboard.resetRoomsSuccess',
            '{{count}} rooms reset to Dirty',
            {
              count: data.reset_count,
            },
          ),
          type: 'success',
        })
      },
      onError: () => {
        toast.create({
          title: text(
            'hotels.dashboard.resetRoomsFailed',
            'Failed to reset rooms',
          ),
          type: 'error',
        })
      },
    },
  })

  const handleReset = useCallback(() => {
    resetMutation.mutate({ pbxUuid, pbxId })
  }, [resetMutation, pbxUuid, pbxId])

  const wakeupTonesByRoomId = useMemo(() => {
    const toneMap = new Map<
      number,
      ReturnType<typeof getWakeupNotificationTone>
    >()
    const wakeupResults = wakeupToneQuery.data?.results ?? []

    for (const room of rooms) {
      const roomWakeups = getRoomDashboardWakeups(wakeupResults, room)
      toneMap.set(room.id, getWakeupNotificationTone(roomWakeups))
    }

    return toneMap
  }, [rooms, wakeupToneQuery.data?.results])

  const hasAnyWakeupCounts = useMemo(
    () => rooms.some((room) => (room.wakeup_calls_count ?? 0) > 0),
    [rooms],
  )

  const shouldWaitForWakeupTones =
    hasAnyWakeupCounts &&
    !wakeupToneQuery.data &&
    wakeupToneQuery.isLoading &&
    !wakeupToneQuery.isError

  const emergencyCalls = Array.isArray(emergencyQuery.data)
    ? emergencyQuery.data
    : []
  const incomingCalls = Array.isArray(callHistoryQuery.data)
    ? callHistoryQuery.data
    : []

  return (
    <VStack align="stretch" gap={6}>
      {/* Call history: two-column layout matching legacy */}
      <Grid templateColumns={{ base: '1fr', sm: '2fr 1fr' }} gap={6}>
        <Box order={{ base: 2, sm: 1 }}>
          <Text as="h2" fontWeight="semibold" mb={3}>
            {text(
              'hotels.dashboard.incomingCallHistory',
              'Incoming Call History',
            )}
          </Text>
          <IncomingCallsTable
            calls={incomingCalls}
            isLoading={callHistoryQuery.isLoading}
          />
        </Box>

        <Box order={{ base: 1, sm: 2 }}>
          <Text as="h2" fontWeight="semibold" mb={3}>
            {text('hotels.dashboard.emergencyHistory', '911 History (7 Days)')}
          </Text>
          <EmergencyCallsTable
            calls={emergencyCalls}
            isLoading={emergencyQuery.isLoading}
          />
        </Box>
      </Grid>

      {/* Room status grid — full-width below call history */}
      <Box>
        <HStack justify="space-between" mb={4}>
          <Text as="h2" fontWeight="semibold">
            {text('hotels.dashboard.roomStatus', 'Room Status')}
          </Text>
          {permissions.canThumbDownloadQueues && (
            <ResetAllRoomsButton
              onReset={handleReset}
              isResetting={resetMutation.isPending}
            />
          )}
        </HStack>
        {roomsQuery.isLoading || shouldWaitForWakeupTones ? (
          <Grid templateColumns="repeat(auto-fill, minmax(190px, 1fr))" gap={3}>
            {Array.from({ length: ROOM_SKELETON_COUNT }).map((_, index) => (
              <Box
                key={`room-skeleton-${index}`}
                p={3}
                borderWidth={2}
                borderColor="border.default"
                borderRadius="l2"
                bg="bg.subtle"
              >
                <VStack align="stretch" gap={2}>
                  <HStack justify="space-between">
                    <Skeleton h="18px" w="50px" />
                    <Skeleton h="14px" w="70px" />
                  </HStack>
                  <Skeleton h="14px" w="60%" />
                  <HStack justify="flex-end">
                    <Skeleton h="30px" w="74px" borderRadius="md" />
                  </HStack>
                </VStack>
              </Box>
            ))}
          </Grid>
        ) : roomsQuery.isError ? (
          <ErrorState
            title={text('hotels.errors.loadRooms', 'Failed to load rooms')}
            retryLabel={text('hotels.common.retry', 'Try again')}
            onRetry={() => roomsQuery.refetch()}
          />
        ) : (
          <RoomStatusGrid
            rooms={rooms}
            api={api}
            pbxId={pbxId}
            pbxUuid={pbxUuid}
            pbxTimeZone={pbxTimeZone}
            toast={toast}
            permissions={permissions}
            wakeupTonesByRoomId={wakeupTonesByRoomId}
          />
        )}
      </Box>
    </VStack>
  )
}
